// Single writer (Implementation Plan v02 §5.1, IR-37; I-1a Batch 3). Only one
// open copy of the app writes at a time: it holds a Web Lock for as long as the
// store is open. A second tab or window does not wait — it opens read-only with
// a calm line. The lock manager is a parameter so tests can simulate two copies.

export type LockManagerLike = Pick<LockManager, 'request'>

export const WRITER_LOCK_NAME = 'pebble-writer'

export interface WriterLock {
  readonly held: boolean
  release(): void
}

const NOT_HELD: WriterLock = { held: false, release: () => undefined }

function defaultLocks(): LockManagerLike | undefined {
  return typeof navigator !== 'undefined' ? navigator.locks : undefined
}

export function acquireWriterLock(
  locks: LockManagerLike | undefined = defaultLocks(),
  name: string = WRITER_LOCK_NAME,
): Promise<WriterLock> {
  if (!locks) {
    // Web Locks is in every supported browser (Chrome 120+ floor, Safari 15.4+).
    // Without it single-writer cannot be enforced; stay writable rather than
    // locking the person out of their own diary.
    return Promise.resolve({ held: true, release: () => undefined })
  }
  return new Promise<WriterLock>((resolve, reject) => {
    locks
      .request(name, { ifAvailable: true }, (lock) => {
        if (!lock) {
          resolve(NOT_HELD)
          return undefined
        }
        // Hold the lock until release() is called.
        return new Promise<void>((done) => {
          let released = false
          resolve({
            held: true,
            release: () => {
              if (!released) {
                released = true
                done()
              }
            },
          })
        })
      })
      .catch(reject)
  })
}
